import { shouldPreserveQueryParam } from "./book-demo";

export const companyTypeOptions = ["broker_3pl", "shipper", "freight_tech", "carrier", "other"] as const;
export const monthlyLoadVolumeOptions = ["under_25", "25_100", "101_500", "500_plus"] as const;
export const biggestPainOptions = ["exceptions", "carrier_vetting", "tracking", "documents", "ai_ops", "api", "other"] as const;
export const timelineOptions = ["now", "this_quarter", "exploring"] as const;
export const productInterestOptions = ["haulbase-agent", "haulbase-atms", "headless-haulbase", "not_sure"] as const;

export type CompanyType = (typeof companyTypeOptions)[number];
export type MonthlyLoadVolume = (typeof monthlyLoadVolumeOptions)[number];
export type BiggestPain = (typeof biggestPainOptions)[number];
export type DemoTimeline = (typeof timelineOptions)[number];
export type ProductInterest = (typeof productInterestOptions)[number];
export type LeadQuality = "high" | "medium" | "low";

export type DemoLeadInput = {
  name?: unknown;
  email?: unknown;
  company?: unknown;
  role?: unknown;
  companyType?: unknown;
  monthlyLoadVolume?: unknown;
  currentWorkflow?: unknown;
  biggestPain?: unknown;
  timeline?: unknown;
  productInterest?: unknown;
  notes?: unknown;
  sourcePath?: unknown;
  sourceSearch?: unknown;
  referrer?: unknown;
};

export type PreparedDemoLead = {
  name: string;
  email: string;
  company: string;
  role: string;
  companyType: CompanyType;
  monthlyLoadVolume: MonthlyLoadVolume;
  currentWorkflow: string;
  biggestPain: BiggestPain;
  timeline: DemoTimeline;
  productInterest: ProductInterest;
  notes?: string;
  sourcePath?: string;
  score: number;
  quality: LeadQuality;
  calendarEligible: boolean;
  attribution: Record<string, string>;
};

export type DemoLeadPreparationResult =
  | { ok: true; lead: PreparedDemoLead }
  | { ok: false; errors: string[] };

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function prepareDemoLeadSubmission(input: DemoLeadInput): DemoLeadPreparationResult {
  const lead = {
    name: asTrimmedString(input.name),
    email: asTrimmedString(input.email).toLowerCase(),
    company: asTrimmedString(input.company),
    role: asTrimmedString(input.role),
    companyType: asOption(input.companyType, companyTypeOptions),
    monthlyLoadVolume: asOption(input.monthlyLoadVolume, monthlyLoadVolumeOptions),
    currentWorkflow: asTrimmedString(input.currentWorkflow),
    biggestPain: asOption(input.biggestPain, biggestPainOptions),
    timeline: asOption(input.timeline, timelineOptions),
    productInterest: asOption(input.productInterest, productInterestOptions),
    notes: optionalTrimmedString(input.notes),
    sourcePath: optionalTrimmedString(input.sourcePath),
    attribution: buildLeadAttribution(input.sourceSearch, input.referrer),
  };

  const errors = requiredFieldErrors(lead);
  if (errors.length > 0) return { ok: false, errors };
  const { companyType, monthlyLoadVolume, biggestPain, timeline, productInterest } = lead;
  if (!companyType || !monthlyLoadVolume || !biggestPain || !timeline || !productInterest) {
    return { ok: false, errors };
  }

  const score = scoreLeadFit({ companyType, monthlyLoadVolume, biggestPain, timeline, productInterest });
  const quality = qualityFromScore(score);

  return {
    ok: true,
    lead: {
      name: lead.name,
      email: lead.email,
      company: lead.company,
      role: lead.role,
      companyType,
      monthlyLoadVolume,
      currentWorkflow: lead.currentWorkflow,
      biggestPain,
      timeline,
      productInterest,
      notes: lead.notes,
      sourcePath: lead.sourcePath,
      attribution: lead.attribution,
      score,
      quality,
      calendarEligible: quality === "high" || quality === "medium",
    },
  };
}

function requiredFieldErrors(input: {
  name: string;
  email: string;
  company: string;
  role: string;
  companyType?: CompanyType;
  monthlyLoadVolume?: MonthlyLoadVolume;
  currentWorkflow: string;
  biggestPain?: BiggestPain;
  timeline?: DemoTimeline;
  productInterest?: ProductInterest;
}) {
  const errors: string[] = [];
  if (!input.name) errors.push("name");
  if (!emailPattern.test(input.email)) errors.push("email");
  if (!input.company) errors.push("company");
  if (!input.role) errors.push("role");
  if (!input.companyType) errors.push("companyType");
  if (!input.monthlyLoadVolume) errors.push("monthlyLoadVolume");
  if (!input.currentWorkflow) errors.push("currentWorkflow");
  if (!input.biggestPain) errors.push("biggestPain");
  if (!input.timeline) errors.push("timeline");
  if (!input.productInterest) errors.push("productInterest");
  return errors;
}

function scoreLeadFit(input: {
  companyType?: CompanyType;
  monthlyLoadVolume?: MonthlyLoadVolume;
  biggestPain?: BiggestPain;
  timeline?: DemoTimeline;
  productInterest?: ProductInterest;
}) {
  let score = 0;

  score += weighted(input.companyType, {
    broker_3pl: 30,
    shipper: 18,
    freight_tech: 22,
    carrier: 4,
    other: 0,
  });
  score += weighted(input.monthlyLoadVolume, {
    under_25: 0,
    "25_100": 12,
    "101_500": 22,
    "500_plus": 28,
  });
  score += weighted(input.biggestPain, {
    exceptions: 18,
    carrier_vetting: 16,
    tracking: 16,
    documents: 12,
    ai_ops: 16,
    api: 14,
    other: 0,
  });
  score += weighted(input.timeline, {
    now: 18,
    this_quarter: 12,
    exploring: 2,
  });
  score += weighted(input.productInterest, {
    "haulbase-agent": 8,
    "haulbase-atms": 8,
    "headless-haulbase": 8,
    not_sure: 4,
  });

  return Math.min(score, 100);
}

function qualityFromScore(score: number): LeadQuality {
  if (score >= 75) return "high";
  if (score >= 45) return "medium";
  return "low";
}

function buildLeadAttribution(sourceSearch: unknown, referrer: unknown) {
  const attribution: Record<string, string> = {};
  const search = asTrimmedString(sourceSearch);
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);

  for (const [key, value] of params.entries()) {
    if (shouldPreserveQueryParam(key) && value) attribution[key] = value;
  }

  const normalizedReferrer = optionalTrimmedString(referrer);
  if (normalizedReferrer) attribution.referrer = normalizedReferrer;

  return attribution;
}

function asTrimmedString(value: unknown) {
  return typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "";
}

function optionalTrimmedString(value: unknown) {
  const normalized = asTrimmedString(value);
  return normalized || undefined;
}

function asOption<const T extends readonly string[]>(value: unknown, options: T): T[number] | undefined {
  return typeof value === "string" && options.includes(value) ? value : undefined;
}

function weighted<T extends string>(value: T | undefined, weights: Record<T, number>) {
  return value ? weights[value] : 0;
}
